import { useMatches } from "@tanstack/react-router";
import { useEffect } from "react";

interface RouteTitleMatch {
  readonly meta?: unknown;
}

function resolveRouteDocumentTitle(
  matches: readonly RouteTitleMatch[],
): string | null {
  for (let matchIndex = matches.length - 1; matchIndex >= 0; matchIndex -= 1) {
    const meta = matches[matchIndex]?.meta;
    if (!Array.isArray(meta)) {
      continue;
    }

    for (let metaIndex = meta.length - 1; metaIndex >= 0; metaIndex -= 1) {
      const descriptor: unknown = meta[metaIndex];
      if (
        typeof descriptor === "object" &&
        descriptor !== null &&
        "title" in descriptor &&
        typeof descriptor.title === "string"
      ) {
        return descriptor.title;
      }
    }
  }

  return null;
}

/**
 * Sole route-driven writer of the *visible* document title.
 *
 * Start owns the document head. This effect also reconciles imperative live
 * price titles when leaving an asset route.
 *
 * The passive effect is load-bearing: React flushes unmount cleanups before
 * mount/update effects in the same pass, so this write lands after an
 * unmounting page restores its captured title (see the symbol page's
 * `useLiveQuoteDocumentTitle`) and after head reconciliation. In-page
 * imperative writers such as the live quote stay visible because this effect
 * re-fires only when the *route* title changes.
 */
export function RouterDocumentTitleSync() {
  const title = useMatches({
    select: resolveRouteDocumentTitle,
  });

  useEffect(() => {
    if (title !== null) {
      document.title = title;
    }
  }, [title]);

  return null;
}
